import { useSidebar } from "../context/SidebarContext";
import { createPortal } from "react-dom";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import AppSidebar from "./AppSidebar";
import { useFamilyMode } from "../context/FamilyModeContext";
import FamilyModeExitModal from "../components/familyMode/FamilyModeExitModal";
import { useState, useEffect } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, signInWithCredential } from "firebase/auth";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { useToast } from "../components/ui/toast/ToastProvider";
import { useTrialExpiration } from "../hooks/useTrialExpiration";
import { useTranslation } from "react-i18next";
import { Capacitor } from '@capacitor/core';
import { useGoogleAuth } from "../hooks/useGoogleAuth";
// import TutorialBanner from "../components/onboarding/TutorialBanner"; // OCULTO TEMPORALMENTE - No eliminar
// NotificationPrompt eliminado - se usa solo NotificationPermissionModal
import OnboardingChecklist from "../components/onboarding/OnboardingChecklist";
import { Button } from "../components/ui/Button";
import FeatureUnlockedModal from "../components/features/FeatureUnlockedModal";
import { useFeatureUnlockedModalState, closeFeatureUnlockedModal } from "../hooks/useFeatureUnlock";
import { TimerManager } from "../components/timers/TimerManager";
import { FloatingTimerWidget } from "../components/timers/FloatingTimerWidget";
import { useDeviceRegistry } from "../hooks/useDeviceRegistry";
import { useCallListener } from "../hooks/useCallListener";
// import PrimaryDevicePromptModal from "../components/calls/PrimaryDevicePromptModal";
import { useGooglePlayBillingListener } from "../hooks/useGooglePlayBillingListener";
import { useActiveFamily } from "../context/ActiveFamilyContext";
import { useFamilyPermissions } from "../context/FamilyPermissionsContext";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { useFeatureGate } from "../hooks/useFeatureGate";
import { Phone } from "lucide-react";
import CallModal from "../components/calls/CallModal";
import { UpgradePlanModal } from "../components/subscription/UpgradePlanModal";

const LayoutContent: React.FC = () => {
  const { isMobileOpen, closeMobileSidebar } = useSidebar();
  const { enabled: familyMode, exitModalOpen, openExitModal, closeExitModal, disable } = useFamilyMode();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const location = useLocation();
  const featureModalState = useFeatureUnlockedModalState();
  const { activeFamily } = useActiveFamily();
  const { permissions, loading: permissionsLoading } = useFamilyPermissions();
  const { isFeatureBlocked } = usePlanLimits();
  const { showUpgradeModal, blockedFeatureInfo, closeUpgradeModal, promptUpgrade } = useFeatureGate();
  const [callModalOpen, setCallModalOpen] = useState(false);

  const openCalls = () => {
    if (isFeatureBlocked('familyCalls' as any)) {
      promptUpgrade(
        'Llamadas',
        'Accede al sistema de llamadas familiares (Casa / Miembro).'
      );
      return;
    }
    setCallModalOpen(true);
  };

  const {
    needsPrimaryDeviceConfirm: _needsPrimaryDeviceConfirm,
    setPrimaryDeviceConfirmed: _setPrimaryDeviceConfirmed,
  } = useDeviceRegistry();
  
  // Listen for incoming calls
  useCallListener();

  // Listen for Google Play Billing purchase callbacks (Android native)
  useGooglePlayBillingListener();

  useTrialExpiration();

  // En mobile: cerrar menú lateral al cambiar de pantalla (especialmente al entrar/salir de /calls)
  // para evitar que quede abierto sobre nuevas vistas y genere "saltos" al montar componentes.
  useEffect(() => {
    if (!isMobileOpen) return;
    const isMobile = (() => {
      try {
        return window.innerWidth < 1200;
      } catch {
        return true;
      }
    })();
    if (!isMobile) return;

    closeMobileSidebar();
  }, [location.pathname]);

  const [syncingMembersCount, setSyncingMembersCount] = useState(0);
  const [localSyncing, setLocalSyncing] = useState(false);
  const [syncType, setSyncType] = useState<'sync' | 'disconnect'>('sync');

  useEffect(() => {
    const handleSyncStatus = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.silent) return; // Ignorar sincronizaciones automáticas/silenciosas
      const isSyncing = detail?.syncing ?? false;
      const type = detail?.type ?? 'sync';
      setLocalSyncing(isSyncing);
      if (isSyncing) {
        setSyncType(type);
      }
    };
    window.addEventListener('gcal-sync-status', handleSyncStatus);
    return () => window.removeEventListener('gcal-sync-status', handleSyncStatus);
  }, []);

  useEffect(() => {
    const famId = activeFamily?.id;
    if (!famId) {
      setSyncingMembersCount(0);
      return;
    }
    const q = query(collection(db, 'members'), where('familyId', '==', famId));
    const unsub = onSnapshot(q, (snap) => {
      const count = snap.docs.filter((doc) => {
        const data = doc.data();
        return data?.integrations?.google?.lastSyncStatus === 'syncing';
      }).length;
      setSyncingMembersCount(count);
    }, (error) => {
      console.warn('⚠️ Error listening to members syncing state in layout:', error);
      setSyncingMembersCount(0);
    });
    return () => unsub();
  }, [activeFamily?.id]);

  // ✅ Redirigir a HomeFamiliar cuando la app se inicia en modo grupo
  useEffect(() => {
    // En modo grupo solo restringir navegación para usuarios SIN permisos de admin/owner.
    // Si sos admin/owner, no forzar redirect en refresh.
    if (!activeFamily?.id) return;
    if (permissions.role === null) return;

    if (familyMode && !permissionsLoading && !permissions.isAdmin) {
      // Lista de rutas permitidas en modo grupo
      const allowedRoutes = [
        '/groups/',
        '/groups',
        '/member/',
        '/member',
        '/family/', // Planificador/recetas
        '/family',
        '/rewards/',
        '/rewards',
        '/calendar',
        '/preferences',
        '/settings/data-management', // Gestión de datos - cualquier usuario puede acceder
        '/notifications',
        '/profile',
        '/calls',
      ];
      
      // Si está en modo grupo y NO está en una ruta permitida, redirigir a home
      const isAllowedRoute = allowedRoutes.some(route => location.pathname.startsWith(route));
      
      if (!isAllowedRoute) {
        // Usar setTimeout para evitar problemas de timing en Android/iOS
        const timer = setTimeout(() => {
          navigate('/groups/home-groups', { replace: true });
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [familyMode, permissionsLoading, permissions.isAdmin, permissions.role, activeFamily?.id, location.pathname, navigate]); // Ejecutar cuando cambia modo o ruta

  // Inline re-auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Inicializar Google Auth para móvil
  const { isReady: googleAuthReady } = useGoogleAuth();
  const [error, setError] = useState<string | null>(null);

  const checkAdminAndExit = async (uid: string) => {
    try {
      const userDocRef = doc(db, "users", uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        setError("UserNotFound");
        return;
      }

      const userData = userDocSnap.data();
      const role = userData?.role;
      // Validar contra la familia activa (source of truth). Fallbacks para cold start.
      const fallbackActiveFamilyId = (() => {
        try {
          return localStorage.getItem("activeFamilyId");
        } catch {
          return null;
        }
      })();
      const targetFamilyId: string | null =
        (activeFamily?.id as string | undefined) ||
        (userData?.activeFamilyId as string | undefined) ||
        (fallbackActiveFamilyId as string | null) ||
        (userData?.familyId as string | undefined) ||
        null;

      // Superadmin
      if (role === "admin" || role === "superadmin") {
        disable();
        closeExitModal();
        showToast({ type: "success", message: t("common.deactivated") });
        navigate("/groups/dashboard", { replace: true });
        return;
      }

      // Owner o admin de su familia
      if (targetFamilyId) {
        const familyDocRef = doc(db, "families", targetFamilyId);
        const familyDocSnap = await getDoc(familyDocRef);
        if (familyDocSnap.exists()) {
          const familyData = familyDocSnap.data();
          const isOwner = familyData.ownerId === uid;
          const isAdmin = Array.isArray(familyData.admins) && familyData.admins.includes(uid);

          if (isOwner || isAdmin) {
            disable();
            closeExitModal();
            showToast({ type: "success", message: t("common.familyModeDeactivated") });
            navigate("/groups/dashboard", { replace: true });
            return;
          }
        }
      }

      setError("AdminRequired");
      showToast({ type: "error", message: t("common.noPermission") });
    } catch (err: any) {
      setError(err?.message || t("common.errorCheckingPermissions"));
    }
  };

  return (
      <div className="h-[var(--app-height)] overflow-hidden overflow-x-hidden xl:flex bg-[#FFF5EB] dark:bg-gray-950 relative transition-colors duration-500">
      {/* Header global - Ahora absoluto al nivel de la app para que ocupe todo el ancho y no sea cortado por el sidebar */}
      <div className="absolute top-0 left-0 right-0 z-[100] pointer-events-none">
        <div className="pointer-events-auto">
          <AppHeader />
        </div>
      </div>

      <div>
        <AppSidebar />
        <Backdrop />
      </div>

      <div
        className="flex-1 h-[var(--app-height)] overflow-hidden overflow-x-hidden flex flex-col bg-transparent ml-0 nb:ml-[264px] 2xl:ml-[344px] relative"
      >
        {/* Tutorial Banner - OCULTO TEMPORALMENTE - No eliminar */}
        {/* <TutorialBanner /> */}

        {/* Contenido: El scroll ocurre aquí. El padding compensa el header global sin cortarlo */}
        {(location.pathname.includes('calendar-v2')) ? (
          // Layout especial para Calendar V2 (beta)
          <div className={`flex-1 overflow-hidden ${exitModalOpen ? "opacity-50 pointer-events-none" : ""}`}>
            <Outlet />
          </div>
        ) : (
          // Layout normal para otras páginas
          <div className={`flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide ${exitModalOpen ? "opacity-50 pointer-events-none" : ""}`}>
            <div className={`mx-auto overflow-x-hidden ${location.pathname.includes('calendar') ? 'max-w-[1920px]' : 'max-w-[1440px]'} pb-32 sm:pb-36 md:pb-40 nb:pb-6 sm:p-4 md:p-6 pt-28 md:pt-24 nb:pt-20`}>
              <Outlet />
            </div>
          </div>
        )}
      </div>

      {/* Exit Family Mode Modal - Nuevo sistema con PIN */}
      <FamilyModeExitModal />
      
      {/* Feature Unlocked Modal - Invitación al desbloquear */}
      {featureModalState.featureName && (
        <FeatureUnlockedModal
          isOpen={featureModalState.isOpen}
          onClose={closeFeatureUnlockedModal}
          featureName={featureModalState.featureName}
        />
      )}

      {/* Prompt para activar notificaciones - ELIMINADO: ahora solo se usa NotificationPermissionModal al terminar onboarding */}

      {/* Checklist de onboarding - DESACTIVADO, usando OnboardingPage */}
      {/* <OnboardingChecklist /> */}
      
      {/* Sistema de timers globales - Fase 1 y 2 */}
      <TimerManager />
      <FloatingTimerWidget />

      {/* FAB de Llamadas - Solo visible en desktop (nb+) */}
      {activeFamily?.id && (
        <button
          onClick={openCalls}
          className="hidden nb:flex fixed right-8 bottom-8 z-40 w-16 h-16 rounded-full bg-[#EF6E6B] hover:bg-[#EF6E6B]/90 text-white shadow-2xl hover:shadow-[0_20px_50px_rgba(239,110,107,0.5)] transition-all hover:scale-110 active:scale-90 flex items-center justify-center group"
          aria-label={t('calls.title', { defaultValue: 'Llamar' })}
        >
          <Phone className="w-7 h-7" />
          <span className="absolute right-full mr-4 px-3 py-1.5 bg-gray-900 text-white text-xs font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {t('calls.title', { defaultValue: 'Llamar' })}
          </span>
        </button>
      )}

      {/* Global Google Calendar Syncing Progress Card */}
      {localSyncing && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[9990] bg-[#055590] text-white shadow-xl rounded-2xl p-4 w-[280px] border border-white/10 transition-all duration-300 right-4 bottom-24 nb:right-28 nb:bottom-8 pointer-events-auto"
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <svg className="animate-spin text-white shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text-xs font-bold uppercase tracking-wider">
                {syncType === 'disconnect'
                  ? t('preferences.gcalDisconnectingGlobal', { defaultValue: 'Desconectando Google Calendar...' })
                  : t('preferences.gcalSyncingGlobal', { defaultValue: 'Sincronizando Google Calendar...' })}
              </span>
            </div>
            {/* White progress bar loader */}
            <div className="w-full bg-white/20 h-1.5 rounded-full overflow-hidden">
              <div className="bg-white h-full animate-pulse w-full"></div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Llamadas */}
      <CallModal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
      />

      {/* Modal de Upgrade para feature bloqueada */}
      <UpgradePlanModal
        isOpen={showUpgradeModal}
        onClose={closeUpgradeModal}
        feature={blockedFeatureInfo?.name}
        reason={blockedFeatureInfo?.description}
      />

      {/*
      <PrimaryDevicePromptModal
        isOpen={needsPrimaryDeviceConfirm}
        onConfirm={setPrimaryDeviceConfirmed}
      />
      */}

    </div>
  );
};

const AppLayout: React.FC = () => {
  return <LayoutContent />;
};

export default AppLayout;
